import View from '@play-co/timestep-core/lib/ui/View';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Scroll from 'src/game/components/shared/Scroll';
import StateObserver, { now, user } from 'src/StateObserver';
import { buddiesGetEvent } from 'src/replicant/buddies/buddies.getters';
import { buddiesFetchPlayers, buddiesGetFriends } from '../buddies.friends';
import sounds from 'src/lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import type { BuddiesPlayerMap } from 'src/replicant/buddies/buddies.asyncGetters';
import NotificationBadge from 'src/game/components/shared/NotificationBadge';
import {
  BuddiesFriendEntry,
  BuddiesInviteItemParams,
} from './BuddiesFriendEntry';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';

type BuddiesTabRequestsOpts = {
  tab: View;
  onRefresh: () => Promise<void>;
  requestsBadge: NotificationBadge;
};

export class BuddiesTabRequests {
  private tab: View;
  private scroll: Scroll<BuddiesInviteItemParams, BuddiesFriendEntry>;
  private noRequests: LangBitmapFontTextView;
  private onRefresh: BuddiesTabRequestsOpts['onRefresh'];
  private requestsBadge: NotificationBadge;

  constructor(opts: BuddiesTabRequestsOpts) {
    this.tab = opts.tab;
    this.onRefresh = opts.onRefresh;
    this.requestsBadge = opts.requestsBadge;

    const scrollRect = {
      x: 0,
      y: 42,
      width: this.tab.style.width,
      height: this.tab.style.height - 23,
    };

    this.scroll = new Scroll({
      superview: this.tab,
      createItem: (
        superview: View,
        _i: number,
        data: BuddiesInviteItemParams,
      ) => {
        const entry = new BuddiesFriendEntry({ superview, x: 20 });
        entry.setup({ ...data });
        return entry;
      },
      recycle: false,
      rect: scrollRect,
    });

    this.noRequests = new LangBitmapFontTextView({
      superview: this.tab,
      x: this.tab.style.width / 2,
      y: this.tab.style.height / 2,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 28,
      font: 'Body',
      width: this.tab.style.width - 60,
      height: 150,
      color: '#9C693A',
      wordWrap: true,
      visible: false,
      localeText: () => i18n('match2.buddies.invitePopup.noRequests'),
    });
  }

  public async refresh() {
    const friendsMap = await buddiesGetFriends();
    const event = buddiesGetEvent(user(), now());
    const invitesEntries = Object.entries(event?.invites ?? {});

    const invitesReceived = invitesEntries.filter(
      ([_, inv]) => inv.status === 'inviteReceived',
    );
    const invitesSent = invitesEntries.filter(
      ([_, inv]) => inv.status === 'inviteSent',
    );

    this.updateBadge(invitesReceived.length);

    const getPlayer = await this.resolvePlayerData(invitesEntries, friendsMap);

    const entries: BuddiesInviteItemParams[] = [];
    this.buildReceivedSection(entries, invitesReceived, getPlayer);
    this.buildSentSection(entries, invitesSent, getPlayer);

    this.updateVisibility(entries);
    this.scroll.setItems(entries, false);
  }

  private updateBadge(receivedCount: number) {
    if (receivedCount > 0) {
      this.requestsBadge.setText(receivedCount.toString());
      this.requestsBadge.show();
    } else {
      this.requestsBadge.hide();
    }
  }

  private async resolvePlayerData(
    invitesEntries: [string, unknown][],
    friendsMap: BuddiesPlayerMap,
  ): Promise<(id: string) => { name: string; photo: string }> {
    const missingIds = invitesEntries
      .map(([id]) => id)
      .filter((id) => !friendsMap[id]);

    let extraPlayers: BuddiesPlayerMap = {};
    if (missingIds.length > 0) {
      try {
        extraPlayers = await buddiesFetchPlayers(missingIds);
      } catch {
        // Silently handle - we'll show fallback names
      }
    }

    return (id: string) => ({
      name: friendsMap[id]?.name ?? extraPlayers[id]?.name ?? '...',
      photo: friendsMap[id]?.photo ?? extraPlayers[id]?.photo ?? '',
    });
  }

  private buildReceivedSection(
    entries: BuddiesInviteItemParams[],
    invitesReceived: [string, unknown][],
    getPlayer: (id: string) => { name: string; photo: string },
  ) {
    if (invitesReceived.length === 0) return;

    entries.push({
      title: i18n('match2.buddies.invitePopup.invitesReceived'),
      message: i18n('match2.buddies.invitePopup.invitesReceivedDisclosure'),
    });
    for (const [id] of invitesReceived) {
      const player = getPlayer(id);
      entries.push({
        id,
        name: player.name,
        photo: player.photo,
        status: 'invite-received',
        onAction: async () => {
          sounds.sfx.play('ok');
          await this.acceptInvite(id);
        },
        onSecondaryAction: async () => {
          sounds.sfx.play('ok');
          await this.rejectInvite(id);
        },
      });
    }
  }

  private buildSentSection(
    entries: BuddiesInviteItemParams[],
    invitesSent: [string, unknown][],
    getPlayer: (id: string) => { name: string; photo: string },
  ) {
    if (invitesSent.length === 0) return;

    entries.push({
      title: i18n('match2.buddies.invitePopup.invitesSent'),
    });
    for (const [id] of invitesSent) {
      const player = getPlayer(id);
      entries.push({
        id,
        name: player.name,
        photo: player.photo,
        status: 'invite-sent',
        onAction: async () => {
          sounds.sfx.play('ok');
          await this.removeInvite(id);
        },
      });
    }
  }

  private updateVisibility(entries: BuddiesInviteItemParams[]) {
    if (entries.length === 0) {
      this.noRequests.show();
      this.scroll.getView().hide();
    } else {
      this.noRequests.hide();
      this.scroll.getView().show();
    }
  }

  private async acceptInvite(senderId: string) {
    await withLoading(
      async () => {
        const result = await StateObserver.invoke.buddiesAcceptInvite({
          senderId,
        });
        if (!result.success) {
          await this.showError(result.reason);
        }
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_REQUESTS,
        superview: this.tab,
      },
    );
  }

  private async rejectInvite(senderId: string) {
    await withLoading(
      async () => {
        await StateObserver.invoke.buddiesRemoveInvite({ id: senderId });
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_REQUESTS,
        superview: this.tab,
      },
    );
  }

  private async removeInvite(id: string) {
    await withLoading(
      async () => {
        await StateObserver.invoke.buddiesRemoveInvite({ id });
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_REQUESTS,
        superview: this.tab,
      },
    );
  }

  private async showError(error?: string) {
    if (!error) error = 'unknown';
    await openPopupPromise('popupModalWhite', {
      title: i18n('match2.buddies.errorPopup.title'),
      message: i18n(`match2.buddies.errorPopup.${error}`),
      tracking: { feature: 'buddies', $subFeature: `error_${error}` },
    });
  }
}
